<template>
  <div>
    <v-progress-linear
      v-if="loading"
      indeterminate
    />
    <v-card>
      <v-toolbar
        flat
        color="primary"
        dark
        dense
      >
        <v-icon left>mdi-palette</v-icon>
        Branding
        <v-spacer />
      </v-toolbar>
      <v-card-text
        v-if="!loading"
      >
        <v-text-field
          v-model="orgSettings.name"
          label="Organization Name"
        />
        <v-text-field
          v-model="orgSettings.subdomain"
          label="Organization Subdomain"
          :suffix="'.' + getRootDomain()"
          disabled
        />
        <v-text-field
          v-model="orgSettings.app_name"
          label="App Name"
          hint="Replace 'CaseLocker' with your own label in emails and on the site"
          placeholder="CaseLocker"
          persistent-hint
        />
        <br />
        <v-layout row wrap>
          <v-flex sm3 style="text-align:center;">
            <span>Primary Brand Color:
              <v-tooltip bottom>
                  <small
                    slot="activator"
                  >
                    <v-icon
                      size="14"
                    >
                      info
                    </v-icon>
                  </small>
                <span>Your primary brand color is used for the navigation bar.</span>
              </v-tooltip>
            </span>
            <sketch-picker
              @input="updatePrimary"
              :value="orgSettings.primary_color || '#0d47a1'"
              style="margin: auto;"
            ></sketch-picker>
          </v-flex>
          <v-flex sm3 style="text-align:center;">
            <span>Secondary Brand Color:
              <v-tooltip bottom>
                <small
                  slot="activator"
                >
                  <v-icon
                    size="14"
                  >
                    info
                  </v-icon>
                </small>
                <span>Your secondary brand color is currently used as the background for contact logins.</span>
              </v-tooltip>
            </span>
            <sketch-picker
              @input="updateSecondary"
              :value="orgSettings.secondary_color || '#ffffff'"
              style="margin: auto;"
            ></sketch-picker>
          </v-flex>
          <v-flex sm3 style="text-align:center;position:relative;"
            class="logo_back"
          >
            <span>Organization Logo:<br />
              <v-tooltip bottom>
                <small
                  slot="activator"
                >
                  Maximum Recommended Size: 1536px x 768px
                  <v-icon
                    size="14"
                  >
                    info
                  </v-icon>
                </small>
                <span>This is the maximum recommended size for your logo.<br />
                      Your logo will automatically be constrained on pages it is displayed.<br />
                      Large images may increase load time and become distorted if constrained too far. </span>
              </v-tooltip>
            </span><br />
            <img
              :src="logoUrl"
              alt="Organization Logo"
              style="max-width: 100%;"
            />
            <div
              class="uploadHover"
              @click="uploadDialog=true;uploadIcon=false;"
            >
              <v-icon
                color="primary"
                size="164"
              >
                cloud_upload
              </v-icon>
              <br />
              <span>
                Click to Replace Image
              </span>
            </div>
          </v-flex>
          <v-flex sm3 style="text-align:center;position:relative;">
            <span>Organization Icon:<br />
              <v-tooltip bottom>
                <small
                  slot="activator"
                >
                  Recommended Size: 512px x 512px
                  <v-icon
                    size="14"
                  >
                    info
                  </v-icon>
                </small>
                <span>This is the recommended size for your icon.<br />
                      Your icon will automatically be constrained on pages it is displayed.<br />
                      Large images may increase load time and become distorted if constrained too far. </span>
              </v-tooltip>
            </span><br />
            <img
              :src="iconUrl"
              alt="Organization Icon"
              style="max-width: 100%;"
            />
            <div
              class="uploadHover"
              @click="uploadDialog=true;uploadIcon=true;"
            >
              <v-icon
                color="primary"
                size="164"
              >
                cloud_upload
              </v-icon>
              <br />
              <span>
                Click to Replace Image
              </span>
            </div>
          </v-flex>
        </v-layout>
      </v-card-text>
      <v-dialog
        v-model="uploadDialog"
        width="500"
      >
        <v-card>
          <form method="post">
            <v-toolbar
              color="primary"
              dark
              flat
              dense
            >
              Upload New {{uploadIcon ? 'Icon' : 'Logo'}}
            </v-toolbar>
            <v-card-text>
              <div style="text-align:Center;display:block;width:100%;height:32px;color:lightgrey;font-size:18px;">
                <input
                  type="file"
                  accept="image/*"
                  ref="files"
                  v-on:change="handleFileUploads()"
                />
              </div>
              <br />
              <div 
                v-if="!isAnImage"
                style="text-align: center; font-size: 18px"
              >
                <v-icon
                  color="red"
                >
                  mdi-exclamation
                </v-icon>
                <span
                  style="color: red"
                  v-html="invalidFileMessage"
                >
                </span>
              </div>
            </v-card-text>
          </form>
          <v-card-actions>
            <v-spacer />
            <v-btn
              :disabled="!files"
              color="blue"
              @click="submitFiles()"
            >
              <v-icon left>cloud_upload</v-icon> Upload
            </v-btn>
            <v-spacer />
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="loading"
          color="primary"
          @click="save"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import api from '@/api'
import Editor from '@/components/admin/Editor'
import { Sketch } from 'vue-color'
import appMixin from '@/components/global/appmixin.js'

export default {
  name: 'OrgBranding',
  mixins: [
    appMixin
  ],
  components: {
    Editor,
    'sketch-picker': Sketch,
  },
  data () {
    return {
      loading: false,
      orgSettings: {},
      uploadDialog: false,
      uploadIcon: false,
      files: '',
      searchUsers: '',
      users: [],
      isAnImage: true,
      nonCompatibleImageTypes: [
        'tiff',
        'svg+xml'
      ],
      invalidFileMessage: `File is not compatible <br /> Please upload a PNG, JPEG, or GIF`
    }
  },
  created () {
    this.documentTitle('Organization Settings')
    this.fetch()
  },
  watch: {
    searchUsers () {
      if (this.searchUsers) {
        this.fetchUsers()
      }
    }
  },
  methods: {
    fetch () {
      this.loading = true
      api.auth.organization.get()
        .then(org => {
          this.orgSettings = org
          delete this.orgSettings.logo
          delete this.orgSettings.icon
          if (org.default_receiver) {
            this.fetchUsers(org.default_receiver)
          }
          this.loading = false
        })
    },
    fetchUsers (id = null) {
      if (id) {
        api.users.user.get(id)
          .then(data => {
            this.users = [data]
          })
      } else {
        api.users.user.list({
          search: this.searchUsers, count: 10
        })
          .then(data => {
            this.users = data.results
          })
      }
    },
    customFilter (item, queryText, itemText) {
      const lname = item.last_name.toLowerCase()
      const fname = item.first_name.toLowerCase()
      const email = item.email.toLowerCase()
      const searchText = queryText.toLowerCase()

      return lname.indexOf(searchText) > -1 ||
        fname.indexOf(searchText) > -1 ||
        email.indexOf(searchText) > -1
    },
    save () {
      this.failed = false
      this.loading = true
      if (!this.orgSettings.app_name)
        this.orgSettings.app_name = "CaseLocker"
      api.auth.organization.save(this.orgSettings)
        .then(() => {
          this.$router.go()
        })
        .catch(() => {
          alert('Something went wrong attempting to update organization settings.')
          this.loading = false
        })
    },
    updatePrimary (value) {
      this.orgSettings.primary_color = value.hex8
      try {
        var element = document.querySelector(".main_app_toolbar")
        element.className = element.className.replace(/\bmain_app_toolbar\b/g, "main_app_toolbar_live")
      }
      catch (err) {
        // do nothing
      }
      document.querySelector(".main_app_toolbar_live").style.background = value.hex8;
    },
    updateSecondary (value) {
      this.orgSettings.secondary_color = value.hex8
      document.querySelector(".logo_back").style.background = value.hex8;
    },
    handleFileUploads () {
      this.isAnImage = true
      this.files = this.$refs.files.files
    },
    submitFiles () {
      if(this.isFileAnImage(this.files[0])){
        this.isAnImage = true
        this.loading = true
        let formData = new FormData()
        for( var i = 0; i < this.files.length; i++ ){
          let file = this.files[i]
          formData.append(`upload`, file)
        }
        api.auth.organization.updateImage(formData, this.uploadIcon ? 'icon' : 'logo')
          .then(response => {
            this.$router.go()
          })
          .catch(err => {
            this.loading = false
            alert('Something went wrong attempting to save that image.')
          })
      } else {
        this.isAnImage = false
      }
    },
    downloadBounceCsv () {
      this.loading = true
      api.email.problems.downloadCsv()
        .then(response => {
          this.loading = false
          let blob = new Blob([response.data])
          let link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = 'Export_ContactEmailProblems_Report' + '.csv'
          link.click()
        })
    },
    isFileAnImage (file) {
      return file.type.split('/')[0] === 'image' && !this.nonCompatibleImageTypes.includes(file.type.split('/')[1]) 
    }
  }
}
</script>

<style scoped>
  .details span {
    display:block;
    padding:3px;
  }

  .uploadHover {
    position: absolute;
    padding:50px;
    width:100%;
    height:100%;
    top:40px;
    bottom:0;
    background:rgba(255,255,255,0);
    opacity:0.05;
  }

  .uploadHover span {
    font-size:18px;
    text-align:center;
  }

  .uploadHover:hover {
    opacity:1;
    background:rgba(255,255,255,0.5);
  }
</style>
